import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Instagram, Linkedin, Mail, ArrowUpRight } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const socials = [
  { icon: Instagram, href: "https://www.instagram.com/yamper_grow?igsh=MWRuMGI4cmZ5bHNy&utm_source=qr", label: "Instagram" },
  { icon: Linkedin, href: "https://linkedin.com/company/yamper-grow", label: "LinkedIn" },
  { icon: Mail, href: "mailto:yamila.s.vilaverde@gmail.com", label: "Email" },
];

const CTASection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast({ title: t("cta.validation"), variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const { error } = await supabase.from("contact_messages").insert({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });
      if (error) throw error;
      toast({ title: "¡Mensaje enviado!", description: "En breve nos pondremos en contacto." });
      setFormData({ name: "", email: "", message: "" });
    } catch {
      toast({ title: "Error al enviar el mensaje", description: "Intentá de nuevo más tarde.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const title1Words = (t("cta.title1") || "").split(" ");

  return (
    <section id="contacto" className="relative py-24 md:py-32 px-6 bg-foreground text-white overflow-hidden scroll-mt-20" ref={ref}>
      {/* Subtle parallax gradient orb */}
      <motion.div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.06] pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--brand-rosa)) 0%, transparent 70%)",
          y: bgY,
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto">
        <motion.div className="mb-12">
          {/* Tag slide in */}
          <motion.p
            className="text-xs uppercase tracking-[0.25em] font-bold text-brand-rosa mb-4 text-primary-foreground"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {t("cta.tag")}
          </motion.p>

          {/* Word-by-word title reveal */}
          <h2 className="text-4xl md:text-7xl font-black tracking-tight lowercase mb-6">
            {title1Words.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-[0.25em]">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%", rotateX: 40 }}
                  animate={inView ? { y: "0%", rotateX: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  {word}
                </motion.span>
              </span>
            ))}{" "}
            <span className="inline-block overflow-hidden mr-[0.25em]">
              <motion.span
                className="inline-block text-brand-rosa"
                initial={{ y: "110%", rotateX: 40 }}
                animate={inView ? { y: "0%", rotateX: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.15 + title1Words.length * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("cta.title2")}
              </motion.span>
            </span>
            <span className="inline-block overflow-hidden">
              <motion.span
                className="inline-block text-white py-[9px]"
                initial={{ y: "110%", rotateX: 40 }}
                animate={inView ? { y: "0%", rotateX: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.15 + (title1Words.length + 1) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("cta.title3")}
              </motion.span>
            </span>
          </h2>

          {/* Description blur reveal */}
          <motion.p
            className="text-lg md:text-xl text-white/50 max-w-2xl"
            initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            {t("cta.desc")}
          </motion.p>

          {/* Social icons staggered */}
          <div className="flex items-center gap-4 mt-8">
            {socials.map((s, i) => (
              <motion.a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl border border-white/20 flex items-center justify-center text-white/50 hover:text-brand-rosa hover:border-brand-rosa transition-all"
                aria-label={s.label}
                initial={{ opacity: 0, scale: 0, rotate: -20 }}
                animate={inView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.8 + i * 0.1 }}
                whileHover={{ scale: 1.15, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <s.icon size={18} />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Form with staggered field reveals */}
        <motion.form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {[
            <Input
              key="name"
              placeholder={t("cta.name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
              maxLength={100}
            />,
            <Input
              key="email"
              type="email"
              placeholder={t("cta.email")}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
              maxLength={255}
            />,
          ].map((field, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
              animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.6, delay: 1 + i * 0.1 }}
            >
              {field}
            </motion.div>
          ))}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <Textarea
              placeholder={t("cta.message")}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl min-h-[120px]"
              maxLength={1000}
            />
          </motion.div>
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            <motion.button
              type="submit"
              disabled={sending}
              className="relative px-10 py-4 rounded-full bg-white text-foreground text-sm uppercase tracking-[0.15em] font-bold disabled:opacity-50 hover:bg-brand-rosa hover:text-white transition-colors overflow-hidden group"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <span className="relative z-10 flex items-center gap-2">
                {sending ? t("cta.sending") : t("cta.submit")}
                <motion.span
                  className="inline-block"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowUpRight size={16} />
                </motion.span>
              </span>
            </motion.button>
          </motion.div>
        </motion.form>
      </div>
    </section>
  );
};

export default CTASection;
